'use client';

import React from 'react';
import { Menu } from '@miso/design-system';
import { AdminPanelSettingsIcon } from '@/shared/ui/display/EmojiIcon';
import { useAtomValue, useSetAtom } from 'jotai';
import type { UserRole } from '@/entities/auth/model/auth.types';
import { useAuth } from '@/entities/auth/model/useAuth';
import { viewModeAtom, getAvailableViewModes } from '@/entities/workspace-config';

function getRoleLabel(role: UserRole): string {
  switch (role) {
    case 'Administrator':
      return 'Administrator';
    case 'Engineer':
      return 'Engineer';
    case 'Staff':
      return 'Staff';
    case 'Operator':
      return 'Operator';
    case 'Planner':
      return 'Planner';
    default:
      return 'User';
  }
}

/**
 * 권한 모드 변경 서브메뉴 — `<Menu.SubMenu>` 형태.
 * 사용 가능한 뷰 모드가 2개 미만이면 null을 반환한다.
 */
export function ViewModeSwitcherSubMenu() {
  const { user } = useAuth();
  const viewMode = useAtomValue(viewModeAtom);
  const setViewMode = useSetAtom(viewModeAtom);

  if (!user) return null;

  const userRole = user.role ?? 'Planner';
  const canSwitchMode =
    userRole === 'Administrator' ||
    userRole === 'Engineer' ||
    userRole === 'Staff' ||
    userRole === 'Operator';
  const availableModes = canSwitchMode ? getAvailableViewModes(userRole, !!user.plannerId) : [];

  if (availableModes.length < 2) return null;

  return (
    <Menu.SubMenu icon={<AdminPanelSettingsIcon fontSize="small" />} label="권한 모드 변경">
      {availableModes.map((mode) => (
        <Menu.Item key={mode} selected={viewMode === mode} onClick={() => setViewMode(mode)}>
          {getRoleLabel(mode)}
        </Menu.Item>
      ))}
    </Menu.SubMenu>
  );
}
